import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { checkIsBusinessUser } from "selectors/user";
import {
  checkSelectedBusinessRpaMode,
  getCurrentQuickBooksRealmId,
  getCurrentZohoOrganizationId,
  getSelectedBusinessClasses,
  getSelectedBusinessData
} from "selectors/businesses";
import { getLockedTransactions } from "selectors/transactions";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import AccountNames from "const/AccountNames";
import AdvancedTransactionTypes from "const/AdvancedTransactionTypes";
import Arrays from "utils/Arrays";
import Button from "nlib/ui/Button";
import Checkbox from "nlib/ui/Checkbox";
import ConfidenceAppend from "nlib/common/ConfidenceAppend";
import Constants from "const/Constants";
import DataStatuses from "const/DataStatuses";
import DateTimeFormats from "const/DateTimeFormats";
import Document from "./lib/Document";
import DocumentRecogniseStatuses from "const/DocumentRecogniseStatuses";
import LastComment from "mlib/common/LastComment";
import React, { useCallback, useMemo } from "react";
import ReasonInput from "nlib/common/ReasonInput";
import SelectCategoryInput from "nlib/common/SelectCategoryInput";
import SelectContactInput from "nlib/common/SelectContactInput";
import SelectItemInput from "nlib/common/SelectItemInput";
import TransactionAmount from "nlib/common/TransactionAmount";
import TransactionTypes from "const/TransactionTypes";
import Transactions from "utils/Transactions";
import classNames from "classnames";
import moment from "moment";

const ListItem = (props) => {
  const {
    selectable,
    readyToProcess,
    readyToReview,
    closedBookDate,
    readyToApprove,
    transaction,
    transactionState = {},
    selected,
    localReasons,
    refetchTransactions,
    onSelect,
    onChange,
    onItemClick,
    onReasonBlur
  } = props;

  const {
    id: transactionId,
    documentId,
    status,
    amount,
    reason: originalReason,
    type,
    currency,
    usesItems,
    timestamp,
    description,
    lastComment,
    comments,
    amountTotal = null,
    documentAttachment,
    extraData,
    documentPreview,
    aiProcessing,
    lastExportErrors,
    attachmentId,
    attachmentName,
    documentRecogniseStatus
  } = transaction;

  const {
    vendorId,
    reason,
    item,
    class: classValue,
    directCategorySelection,
    category = {},
    address = {},
    address: { name: payeeName = "" } = {}
  } = transactionState;

  const { uiTexts } = useSelector(getTextsData);

  const businessUser = useSelector(checkIsBusinessUser);

  const rpaMode = useSelector(checkSelectedBusinessRpaMode);

  const classes = useSelector(getSelectedBusinessClasses);

  const quickBooksBusiness = !!useSelector(getCurrentQuickBooksRealmId);

  const zohoBusiness = !!useSelector(getCurrentZohoOrganizationId);

  const transactionLocked = !!useSelector(getLockedTransactions)[transactionId];

  const {
    settings: {
      advancedDocumentsWorkflow,
      allowClientPayeeSelection,
      allowApproveWithoutPayee
    } = {}
  } = useSelector(getSelectedBusinessData);

  const advancedType = useMemo(() => Transactions.getTransactionAdvancedType({ type, extraData }), [type, extraData]);

  const needReaction = status === DataStatuses.NEED_REACTION;

  const typeTransfer = advancedType === AdvancedTransactionTypes.TRANSFER;

  const billPayment = advancedType === AdvancedTransactionTypes.BILL_PAYMENT;

  const receivePayment = advancedType === AdvancedTransactionTypes.RECEIVED_PAYMENT;

  const showReasonInput = rpaMode ? needReaction : (businessUser && !directCategorySelection);

  const showItemInput = !showReasonInput && usesItems;

  const showCategoryField = !showReasonInput && !usesItems;

  const seeTheDocument = !!documentId && advancedDocumentsWorkflow;

  const disableInputs = transactionLocked || aiProcessing || (
    businessUser
      ? status !== DataStatuses.NEED_REACTION
      : ((status === DataStatuses.EXPORTED && !quickBooksBusiness) || status === DataStatuses.TO_REPORT)
  );

  const disableCategoryInput = billPayment || receivePayment || disableInputs || seeTheDocument;

  const documentRecogniseError = documentRecogniseStatus === DocumentRecogniseStatuses.ERROR;

  const handleListItemClick = useCallback(() => {
    onItemClick(transactionId);
  }, [transactionId, onItemClick]);

  const handleCheckboxClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleSelectedChange = useCallback(() => {
    onSelect(transactionId, !selected);
  }, [transactionId, selected, onSelect]);

  const handleReasonChange = useCallback((value) => {
    onChange(transactionId, { reason: value });
  }, [transactionId, onChange]);

  const handleCategoryChange = useCallback((value) => {
    onChange(transactionId, { category: value, tags: value.code ? [value.name] : [] });
  }, [transactionId, onChange]);

  const handleItemChange = useCallback((value) => {
    onChange(transactionId, {
      item: value,
      tags: value.id ? [value.name] : [],
      class: Arrays.findById(classes, value.classId, classValue)
    });
  }, [onChange, transactionId, classes, classValue]);

  const handleContactChange = useCallback((value) => {
    onChange(transactionId, { address: value, vendorId: value.id });
  }, [transactionId, onChange]);

  return (
    <>
      <div
        className={classNames(Css.listItem,
          [
            (status === DataStatuses.TO_REVIEW) && Array.isArray(lastExportErrors)
              && !!lastExportErrors.length && Css.negativeRow,
            (readyToApprove || readyToProcess) && closedBookDate && Css.warningRow,
            (readyToReview || readyToProcess) && Css.highlightRow,
            readyToApprove && Css.positiveRow
          ])}>
        <div className={Css.header} onClick={handleListItemClick}>
          <Checkbox
            className={Css.checkBox}
            checked={selected}
            disabled={!selectable || transactionLocked}
            onClick={handleCheckboxClick}
            onChange={handleSelectedChange} />
          <div className={Css.timestamp}>
            {timestamp
              ? moment.utc(timestamp).format(DateTimeFormats.DATE_TEXT)
              : Constants.EMPTY_PLACEHOLDER}
          </div>
          <div className={Css.amountWrap}>
            <TransactionAmount
              className={Css.amount}
              amount={amount}
              type={type}
              currency={currency} />
            {amountTotal !== null && (
              <div className={Css.amountTotal}>
                <span>&nbsp;/&nbsp;</span>
                <TransactionAmount
                  amount={amountTotal}
                  currency={currency} />
              </div>
            )}
          </div>
        </div>
        <div className={Css.body}>
          <div className={Css.inputs}>
            {!typeTransfer && (!businessUser || allowClientPayeeSelection) && (
              <div className={Css.block}>
                <ConfidenceAppend confidence={address?.confidence}>
                  <SelectContactInput
                    useWidget
                    value={address}
                    disabled={disableInputs}
                    active={!!(address.name && !vendorId)}
                    invalid={(!vendorId && (!documentId || !advancedDocumentsWorkflow) && !allowApproveWithoutPayee)}
                    valid={!!(address.name && vendorId)}
                    onlyVendors={type === TransactionTypes.WITHDRAW && zohoBusiness}
                    onlyCustomers={!!usesItems || (!type !== TransactionTypes.WITHDRAW && zohoBusiness)}
                    probablyVendor={type === TransactionTypes.WITHDRAW && !usesItems}
                    onChange={handleContactChange} />
                </ConfidenceAppend>
              </div>
            )}
            {(showReasonInput || showItemInput || showCategoryField) && (
              <div className={Css.block}>
                {showReasonInput
                  ? (
                    <ReasonInput
                      validate
                      value={reason}
                      status={status}
                      disabled={disableInputs}
                      originalReason={originalReason}
                      localReasons={localReasons}
                      onChange={handleReasonChange}
                      onBlur={onReasonBlur} />
                  )
                  : (showItemInput
                    ? (
                      <ConfidenceAppend confidence={item?.confidence}>
                        <SelectItemInput
                          value={item}
                          invalid={!item?.id}
                          valid={!!item?.id}
                          disabled={disableCategoryInput}
                          onChange={handleItemChange} />
                      </ConfidenceAppend>
                    )
                    : (showCategoryField && (
                      <>
                        <ConfidenceAppend confidence={seeTheDocument ? null : category?.confidence}>
                          <SelectCategoryInput
                            paymentType={type}
                            disabled={disableCategoryInput}
                            placeholder={seeTheDocument ? uiTexts.seeTheDocument : undefined}
                            value={seeTheDocument
                              ? {
                                displayName: type === TransactionTypes.WITHDRAW
                                  ? AccountNames.ACCOUNT_PAYABLE
                                  : AccountNames.ACCOUNT_RECEIVABLE
                              }
                              : category}
                            valid={!!category.code}
                            invalid={(!!category.name || !businessUser) && !category.code}
                            assetAccountsOnly={advancedType === AdvancedTransactionTypes.TRANSFER}
                            onChange={handleCategoryChange} />
                        </ConfidenceAppend>
                      </>
                    )))}
              </div>
            )}
          </div>
          <div className={Css.content} onClick={handleListItemClick}>
            <div className={Css.wrapper}>
              {!businessUser && !category?.code && !transaction.category?.code && !!transaction.reason && (
                <div className={classNames(Css.row, Css.highlight)}>
                  <Icons.Chat />
                  <span>{transaction.reason}</span>
                </div>
              )}
              {(businessUser && !allowClientPayeeSelection) && (
                <div
                  className={classNames(Css.row, {
                    [Css.highlight]: !!(payeeName && !vendorId),
                    [Css.valid]: !!(payeeName && vendorId),
                    [Css.invalid]: !payeeName && !allowApproveWithoutPayee
                  })}>
                  {payeeName ? (vendorId ? <Icons.User /> : <Icons.UserPlus />) : <Icons.User />}
                  <span>{payeeName || uiTexts.noPayee}</span>
                </div>
              )}
              <div
                className={classNames(Css.row, { [Css.warning]: !description })}>
                {description ? <Icons.Info /> : <Icons.Warning />}
                <span>{description || `${uiTexts.noBankDescriptionOrMemo} :(`}</span>
              </div>
              {attachmentId
                ? (
                  <div
                    className={Css.row}>
                    <Icons.File />
                    <span>{attachmentName}</span>
                  </div>
                )
                : (!!documentAttachment && (
                  <div
                    className={classNames(Css.row, documentRecogniseError ? Css.error : Css.highlight)}>
                    <Icons.File />
                    <span>{documentAttachment.originalName}</span>
                  </div>
                ))}
            </div>
            {!attachmentId && !usesItems && advancedType !== AdvancedTransactionTypes.TRANSFER
              && (!rpaMode || advancedType === AdvancedTransactionTypes.BANK_FEED) && (
              <Document
                className={Css.attachment}
                disabled={transactionLocked || aiProcessing}
                type={transaction.type}
                advancedType={advancedType}
                status={status}
                vendorId={vendorId}
                transactionId={transactionId}
                documentId={documentId}
                documentPreview={documentPreview}
                refetchTransactions={refetchTransactions}
                documentRecogniseError={documentRecogniseError} />
            )}
            <Button outline>
              {selectable ? <Icons.PencilSimple /> : <Icons.CaretRight />}
            </Button>
          </div>
        </div>
        {!!lastComment && (
          <div className={Css.footer}>
            <div className={Css.content}>
              <LastComment
                transactionId={transactionId}
                hasUnread={!!comments.unread}
                lastComment={lastComment} />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(ListItem);
